import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Loader2, Link, X, CheckCircle } from 'lucide-react';

interface CheckViewFormProps {
    youtubeUrl: string;
    setYoutubeUrl: (url: string) => void;
    handleCheckView: (e: React.FormEvent) => void;
    isLoading: boolean;
}

export const CheckViewForm: React.FC<CheckViewFormProps> = ({
    youtubeUrl,
    setYoutubeUrl,
    handleCheckView,
    isLoading
}) => {
    const { t } = useTranslation('common');

    return (
        <Card className="w-full mb-6 shadow-sm border-blue-100 bg-white">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl text-blue-700">
                    {t('checkView.header.addTitle')}
                </CardTitle>
                <CardDescription>
                    {t('checkView.header.addDesc')}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleCheckView} className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                            <Link size={18} />
                        </div>
                        <Input
                            value={youtubeUrl}
                            onChange={(e) => setYoutubeUrl(e.target.value)}
                            placeholder={t('checkView.form.placeholder')}
                            className="pl-10 pr-10 border-blue-200 focus:border-blue-500 focus:ring-blue-200"
                            disabled={isLoading}
                        />
                        {youtubeUrl && (
                            <button
                                type="button"
                                onClick={() => setYoutubeUrl('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <Button
                        type="submit"
                        disabled={isLoading || !youtubeUrl.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('checkView.form.checking')}
                            </>
                        ) : (
                            <>
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {t('checkView.form.submit')}
                            </>
                        )}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};
